/**
 * Billing Configuration Modal Component
 *
 * Allows admins to setup default values for billing configuration including:
 * - Sign Up Credits (default_signup_credits)
 * - Monthly Limit (default_monthly_allowance)
 */

import React, { useState, useEffect } from 'react';
import { Icon, Button, Text, Input, FormAlert } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { adminApi, handleAdminApiError } from '../services/adminApi';

interface BillingConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface BillingConfigForm {
  default_signup_credits: number;
  default_monthly_allowance: number;
}

export const BillingConfigModal: React.FC<BillingConfigModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState<BillingConfigForm>({
    default_signup_credits: 50,
    default_monthly_allowance: 50
  });

  useEffect(() => {
    if (isOpen) {
      loadCurrentConfig();
    }
  }, [isOpen]);

  const loadCurrentConfig = async () => {
    setLoadingConfig(true);
    setError(null);

    try {
      const response = await adminApi.getBillingConfig();
      setFormData({
        default_signup_credits: response.config.default_signup_credits || 50,
        default_monthly_allowance: response.config.default_monthly_allowance || 50
      });
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(`Failed to load current configuration: ${errorMessage}`);
      console.error('Failed to load billing config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleInputChange = (field: keyof BillingConfigForm, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [field]: numericValue }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      if (formData.default_signup_credits < 0) {
        throw new Error('Sign up credits cannot be negative');
      }
      if (formData.default_monthly_allowance < 0) {
        throw new Error('Monthly limit cannot be negative');
      }

      await adminApi.updateBillingConfig(formData);

      onSuccess?.();
      handleClose();
    } catch (err: any) {
      const errorMessage = handleAdminApiError(err);
      setError(errorMessage);
      console.error('Failed to update billing config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setError(null);
        onClose();
      }, 300);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[420px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div className="flex items-center gap-bg-v2-lg">
            <div className="p-bg-v2-md bg-bg-brand-secondary rounded-bg-v2-lg flex items-center justify-center">
              <Icon name={IconName.Settings} size="md" className="text-text-brand-primary" inheritColor />
            </div>
            <div>
              <Text variant="text-xl" weight="bold">Billing Configuration</Text>
              <p className="text-sm text-text-tertiary">Setup default values for new users</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          {loadingConfig ? (
            <div className="flex items-center justify-center py-bg-v2-4xl">
              <div className="animate-spin rounded-bg-v2-full h-8 w-8 border-b-2 border-border-brand" />
              <span className="ml-bg-v2-lg text-text-secondary">Loading current configuration...</span>
            </div>
          ) : (
            <>
              {error && <FormAlert message={error} variant="error" />}

              <Input
                label={
                  <span className="flex items-center gap-bg-v2-md">
                    <Icon name={IconName.DollarSign} size="sm" variant="success" />
                    Sign Up Credits
                  </span>
                }
                labelClassName="text-sm font-medium"
                type="number"
                min="0"
                step="0.01"
                value={formData.default_signup_credits}
                onChange={(e) => handleInputChange('default_signup_credits', e.target.value)}
                placeholder="50.00"
                disabled={loading}
                helperText="Free credits given to new users upon signup"
              />

              <Input
                label={
                  <span className="flex items-center gap-bg-v2-md">
                    <Icon name={IconName.CalendarDays} size="sm" variant="utility-purple" />
                    Monthly Limit
                  </span>
                }
                labelClassName="text-sm font-medium"
                type="number"
                min="0"
                step="0.01"
                value={formData.default_monthly_allowance}
                onChange={(e) => handleInputChange('default_monthly_allowance', e.target.value)}
                placeholder="50.00"
                disabled={loading}
                helperText="Default monthly allowance for users with monthly plans"
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          <Button
            variant="outline"
            label="Cancel"
            onClick={handleClose}
            disabled={loading || loadingConfig}
          />
          <Button
            variant="primary"
            label={loading ? 'Updating...' : 'Update Configuration'}
            onClick={handleSubmit}
            disabled={loading || loadingConfig}
          />
        </div>
      </div>
    </>
  );
};

export default BillingConfigModal;
